import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import ProjectsService from "../Services/ProjectsService";
import { useMsal } from "@azure/msal-react";
import { toast } from "react-toastify";
import GraphService from "../Services/GraphService";
import FolderModel from "../Models/FolderModel";
import DocumentsService from "../Services/DocumentsService";
import Checkbox from "./Checkbox";

interface SendDocumentsModalProps {
  code: string;
  afterClose: Function;
}

const SendDocumentsModal = (props: SendDocumentsModalProps) => {
  const { instance } = useMsal();

  const graphService = useMemo(() => new GraphService(instance), [instance]);
  const projectsService = useMemo(
    () => new ProjectsService(instance),
    [instance]
  );

  const documentsService = useMemo(
    () => new DocumentsService(instance),
    [instance]
  );

  const [to, setTo] = useState<string>("");
  const [documents, setDocuments] = useState<FolderModel>();
  const [extraInfo, setExtraInfo] = useState<string>("");
  const [selectedDocuments, setSelectedDocuments] = useState<string[]>([]);

  const [visible, setVisible] = useState<boolean>(true);

  const loadData = useCallback(
    async (projectCode: string) => {
      try {
        const toEmailAddress = await projectsService.getCiawEmailAddress(
          projectCode
        );
        const documentsData = await documentsService.get();

        setTo(toEmailAddress);
        setDocuments(documentsData);
      } catch (err) {
        console.log(err);
        toast.error("Fout bij het ophalen van de data.");
      } finally {
      }
    },
    [projectsService, documentsService]
  );

  useEffect(() => {
    loadData(props.code);
  }, [props.code]);

  function toggle() {
    setVisible(!visible);
    props.afterClose();
  }

  async function save() {
    try {
      if (props.code !== null) {
        const user = await graphService.getUserDetails();
        const success = await projectsService.sendDocuments(
          props.code,
          user.mail ?? user.userPrincipalName ?? "",
          to,
          extraInfo,
          selectedDocuments
        );

        if (!success) {
          toast.error("Fout bij versturen van email.");
        } else {
          setVisible(false);
          props.afterClose();
        }
      }
    } catch (err) {
      toast.error("Fout bij het ophalen van de data.");
    }
  }

  function getDocs(folder?: FolderModel): JSX.Element {
    if (folder) {
      return (
        <>
          {folder?.folders.map((f) => (
            <li>
              {f.name}
              <ul>{getDocs(f)}</ul>
            </li>
          ))}
          {folder?.documents.map((d) => (
            <li>
              <Checkbox
                className="my-1 mr-sm-2"
                label={d.name}
                value={d.id}
                checked={
                  selectedDocuments.find((r) => r === d.id) !== undefined
                }
                onChange={(e: any) => {
                  setSelectedDocuments((old) => {
                    let returnValue = [...old];
                    if (e.target.checked) returnValue.push(d.id);
                    else returnValue = returnValue.filter((r) => r !== d.id);
                    return returnValue;
                  });
                }}
              />
            </li>
          ))}
        </>
      );
    }

    return <></>;
  }

  return (
    <Modal isOpen={visible} toggle={toggle} className="mw-100 w-75">
      <ModalHeader toggle={toggle}>
        {props.code} - Documenten naar klant versturen?
      </ModalHeader>
      <ModalBody>
        <form>
          <div className="form-group">
            <label className="my-1 mr-2">Email verzenden naar</label>
            <input
              type="text"
              className="form-control my-1 mr-sm-2"
              id="columnValueInput"
              onChange={(event) => setTo(event.target.value)}
              value={to}
            />
          </div>
          <div className="form-group">
            <label className="my-1 mr-2">Documenten</label>
            <ul>{getDocs(documents)}</ul>
          </div>
          <div className="form-group">
            <label className="my-1 mr-2">Extra informatie</label>
            <textarea
              className="form-control my-1 mr-sm-2"
              title="Extra informatie"
              style={{ height: "200px" }}
              onChange={(e) => setExtraInfo(e.target.value)}
              value={extraInfo}
            ></textarea>
          </div>
        </form>
      </ModalBody>
      <ModalFooter>
        <Button color="primary" onClick={save}>
          Verzenden
        </Button>{" "}
        <Button color="secondary" onClick={toggle}>
          Sluiten
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default SendDocumentsModal;
